// src/modulos/Parametros/UnitOfMeasurePage.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const COLLECTION = 'parameters/data/unidades-medida';
const ITEMS_PER_PAGE = 10;

const UnitOfMeasurePage = () => {
    const can = usePagePermissions('unidades_de_medida');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [conversionFactorInput, setConversionFactorInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [checkingDelete, setCheckingDelete] = useState(false);

    // Hook de Dados
    const {
        data: units,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(COLLECTION, 'name');

    // ============================================================
    // 1. DEFINIR sortedUnits e filteredUnits ANTES dos callbacks
    // ============================================================
    const sortedUnits = useMemo(() => {
        if (!units) return [];

        return [...units].sort((a, b) => {
            const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : null;
            const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : null;

            if (dateA && dateB) {
                return dateB - dateA;
            }
            if (dateA && !dateB) return -1;
            if (!dateA && dateB) return 1;
            return a.name.localeCompare(b.name);
        });
    }, [units]);

    const filteredUnits = useMemo(() => {
        if (!sortedUnits) return [];
        if (!searchTerm) return sortedUnits;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return sortedUnits.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearch) ||
            String(item.conversionFactor).includes(searchTerm.replace(',', '.'))
        );
    }, [sortedUnits, searchTerm]);

    const totalFilteredItems = filteredUnits.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentUnits = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredUnits.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredUnits, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setIsModalOpen(false);
        setNameInput('');
        setConversionFactorInput('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setIsModalOpen(true);
        setNameInput(item.name);
        setConversionFactorInput(item.conversionFactor ? String(item.conversionFactor) : '');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Verificação se unidade está sendo utilizada
    const checkUnitInUse = useCallback(async (unitId) => {
        try {
            const { db } = await import('../../firebaseConfig');
            const { collection, query, where, getDocs } = await import('firebase/firestore');

            const productsRef = collection(db, 'products');
            const q = query(productsRef, where('unitOfMeasureId', '==', unitId));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                return { inUse: true, count: querySnapshot.size, module: 'produtos' };
            }

            const rawMaterialsRef = collection(db, 'raw-materials');
            const q2 = query(rawMaterialsRef, where('unitOfMeasureId', '==', unitId));
            const querySnapshot2 = await getDocs(q2);

            if (!querySnapshot2.empty) {
                return { inUse: true, count: querySnapshot2.size, module: 'matérias-primas' };
            }

            const workOrdersRef = collection(db, 'work-orders');
            const q3 = query(workOrdersRef, where('unitOfMeasureId', '==', unitId));
            const querySnapshot3 = await getDocs(q3);

            if (!querySnapshot3.empty) {
                return { inUse: true, count: querySnapshot3.size, module: 'ordens de serviço' };
            }

            return { inUse: false };
        } catch (err) {
            console.error('Erro ao verificar uso da unidade:', err);
            return { inUse: false, error: err.message };
        }
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);
        setCheckingDelete(true);

        try {
            const usageCheck = await checkUnitInUse(id);

            if (usageCheck.inUse) {
                setFormError(`Não é possível deletar a unidade "${name}". Ela está sendo utilizada em ${usageCheck.count} ${usageCheck.module}.`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                setCheckingDelete(false);
                setTimeout(() => setFormError(''), 5000);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Unidade "${name}" deletada com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const filteredCount = filteredUnits.length - 1;
                if (currentPage > 1 && filteredCount <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Unidade: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (err) {
            setFormError("Erro ao verificar uso da unidade: " + err.message);
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
            setCheckingDelete(false);
        }
    }, [deleteConfirm, checkUnitInUse, deleteDocument, filteredUnits, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("Nome da unidade é obrigatório.");
            return;
        }

        if (!conversionFactorInput || conversionFactorInput.trim() === '') {
            setFormError("Fator de Conversão é obrigatório.");
            return;
        }

        let cleanedInput = conversionFactorInput.replace(/\./g, '').replace(',', '.');

        if (!/^\d*\.?\d*$/.test(cleanedInput)) {
            setFormError("Fator de Conversão deve conter apenas números.");
            return;
        }

        const conversionFactorNumber = parseFloat(cleanedInput);

        if (isNaN(conversionFactorNumber)) {
            setFormError("Fator de Conversão deve ser um número válido.");
            return;
        }

        if (conversionFactorNumber <= 0) {
            setFormError("Fator de Conversão deve ser um número positivo maior que zero.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'Sistema';
        const timestamps = getTimestamps(editingItem, userEmail);

        const data = {
            name: nameInput.trim().toUpperCase(),
            conversionFactor: conversionFactorNumber,
            ...timestamps,
        };

        // Se for edição, preservar createdAt e createdBy
        if (editingItem) {
            if (editingItem.createdAt) {
                data.createdAt = editingItem.createdAt;
                data.createdBy = editingItem.createdBy || 'Sistema';
            }
        }

        setFormLoading(true);

        try {
            let result;

            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                // Verificar duplicidade
                const exists = sortedUnits.some(
                    unit => unit.name.toLowerCase() === data.name.toLowerCase()
                );

                if (exists) {
                    setFormError(`A unidade "${data.name}" já existe no sistema.`);
                    setFormLoading(false);
                    return;
                }
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Unidade editada com sucesso!' : 'Unidade adicionada com sucesso!');
                setTimeout(() => {
                    resetForm();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, conversionFactorInput, editingItem, sortedUnits, addDocument, updateDocument, resetForm]);

    // Resetar página quando o termo de busca mudar
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // Formatação de data
    const formatDate = useCallback((timestamp) => {
        if (!timestamp) return '---';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }, []);

    // Validação do fator de conversão em tempo real
    const handleConversionFactorChange = useCallback((e) => {
        let value = e.target.value;
        value = value.replace(/[^0-9,.]/g, '');
        const dotCount = (value.match(/\./g) || []).length;
        const commaCount = (value.match(/,/g) || []).length;
        if (dotCount > 1 || commaCount > 1) return;
        setConversionFactorInput(value);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Nova Unidade'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Unidade'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="nameInput">
                        Nome da Unidade <span className={styles.required}>*</span>
                        <span className={styles.fieldHint}> (Ex: UN, KG, M, L, CX)</span>
                    </label>
                    <input
                        id="nameInput"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value.toUpperCase())}
                        placeholder="Digite a sigla ou nome da unidade"
                        required
                        className={styles.input}
                        disabled={formLoading}
                        maxLength={20}
                        autoFocus
                    />
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="factorInput">
                        Fator de Conversão <span className={styles.required}>*</span>
                        <span className={styles.fieldHint}> (Base: 1 = Unidade padrão)</span>
                    </label>
                    <input
                        id="factorInput"
                        type="text"
                        value={conversionFactorInput}
                        onChange={handleConversionFactorChange}
                        placeholder="Ex: 1 (unidade base), 1000 (kg para g), 0.001 (g para kg)"
                        required
                        className={styles.input}
                        disabled={formLoading}
                    />
                    <small className={styles.fieldHint}>
                        Exemplos: 1 = Unidade Base | 1000 = 1kg = 1000g | 0.001 = 1g = 0.001kg
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar a unidade "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Unidades de Medida"
                actionText="Adicionar Unidade"
                onAction={handleNewClick}
                disabled={formLoading || checkingDelete}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome ou fator de conversão..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading || checkingDelete}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={4} rows={ITEMS_PER_PAGE} />
                    ) : currentUnits.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📏'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma unidade de medida cadastrada'
                            }
                            message={
                                searchTerm
                                    ? 'Tente outros termos ou limpe a busca.'
                                    : "Clique em 'Adicionar Unidade' para começar."
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '30%' }}>Sigla/Nome</th>
                                            <th style={{ width: '25%' }}>Fator de Conversão</th>
                                            <th style={{ width: '25%' }}>Última Atualização</th>
                                            <th style={{ width: '20%' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentUnits.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.createdBy && (
                                                        <small className={styles.rowMetadata}>
                                                            Criado por: {item.createdBy}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.daysBadge}>
                                                    <span className={styles.badge}>
                                                        {item.conversionFactor?.toLocaleString('pt-BR', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 6
                                                        })}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={styles.rowMetadata}>
                                                        {formatDate(item.updatedAt || item.createdAt)}
                                                    </span>
                                                    {item.updatedBy && (
                                                        <span className={styles.rowMetadata}>
                                                            por: {item.updatedBy}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading || checkingDelete}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading || checkingDelete}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading || checkingDelete}
                                itemsLabel="unidades"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default UnitOfMeasurePage;